/* eslint-disable no-console */

// In-memory cache for geocoded city coordinates to avoid repeated network calls
const coordsCache = {};

/**
 * Clean city strings (e.g. "Indore (Madhya Pradesh)" -> "Indore")
 */
export const cleanCityString = (city) => {
  if (!city) return '';
  const str =
    typeof city === 'object'
      ? city.cityName || city.label || city.name || ''
      : String(city);

  return str
    .replace(/\s*\(.*?\)/g, '')
    .replace(/,\s*India/gi, '')
    .trim();
};

/**
 * Attempt to calculate distance using Google Maps JavaScript API if available
 */
const fetchGoogleMapsDistance = (originStr, destStr) => {
  return new Promise((resolve) => {
    if (typeof window === 'undefined' || !window.google || !window.google.maps) {
      resolve(null);
      return;
    }

    try {
      if (window.google.maps.DistanceMatrixService) {
        const matrixService = new window.google.maps.DistanceMatrixService();
        matrixService.getDistanceMatrix(
          {
            origins: [`${originStr}, India`],
            destinations: [`${destStr}, India`],
            travelMode: window.google.maps.TravelMode.DRIVING,
            unitSystem: window.google.maps.UnitSystem.METRIC,
          },
          (response, status) => {
            if (status === 'OK' && response?.rows?.[0]?.elements?.[0]?.status === 'OK') {
              const element = response.rows[0].elements[0];
              const distKm = Math.round(element.distance.value / 1000);
              const durationDays = element.duration?.value
                ? Math.max(1, Math.round((element.duration.value / 86400) * 10) / 10)
                : Math.max(1, Math.round((distKm / 400) * 10) / 10);
              resolve({
                distanceKm: distKm,
                transitDays: durationDays,
                source: 'google-matrix',
              });
              return;
            }
            resolve(null);
          }
        );
        return;
      }

      if (window.google.maps.DirectionsService) {
        const directionsService = new window.google.maps.DirectionsService();
        directionsService.route(
          {
            origin: `${originStr}, India`,
            destination: `${destStr}, India`,
            travelMode: window.google.maps.TravelMode.DRIVING,
          },
          (result, status) => {
            if (status === 'OK' && result?.routes?.[0]?.legs?.[0]) {
              const leg = result.routes[0].legs[0];
              const distKm = Math.round(leg.distance.value / 1000);
              const durationDays = leg.duration?.value
                ? Math.max(1, Math.round((leg.duration.value / 86400) * 10) / 10)
                : Math.max(1, Math.round((distKm / 400) * 10) / 10);
              resolve({
                distanceKm: distKm,
                transitDays: durationDays,
                source: 'google-directions',
              });
              return;
            }
            resolve(null);
          }
        );
        return;
      }
    } catch (e) {
      console.warn('Google Maps distance calculation error:', e);
    }
    resolve(null);
  });
};

/**
 * Geocode a city using OpenStreetMap Nominatim Free API with Open-Meteo fallback
 */
const getCityCoordinates = async (cityName) => {
  if (!cityName) return null;
  const cleaned = cleanCityString(cityName);
  const cacheKey = cleaned.toLowerCase();

  if (coordsCache[cacheKey]) {
    return coordsCache[cacheKey];
  }

  // 1. Try Nominatim Free Geocoding API
  try {
    const query = encodeURIComponent(`${cleaned}, India`);
    const response = await fetch(
      `https://nominatim.openstreetmap.org/search?format=json&q=${query}&limit=1&countrycodes=in`,
      {
        headers: {
          Accept: 'application/json',
          'User-Agent': 'PolystoxAdmin/1.0',
        },
      }
    );

    if (response.ok) {
      const data = await response.json();
      if (Array.isArray(data) && data.length > 0 && data[0].lat && data[0].lon) {
        const coords = {
          lat: parseFloat(data[0].lat),
          lon: parseFloat(data[0].lon),
        };
        coordsCache[cacheKey] = coords;
        return coords;
      }
    }
  } catch (err) {
    console.warn(`Nominatim geocoding failed for "${cityName}":`, err);
  }

  // 2. Try Open-Meteo Free Geocoding API as fast fallback
  try {
    const query = encodeURIComponent(cleaned);
    const response = await fetch(
      `https://geocoding-api.open-meteo.com/v1/search?name=${query}&count=1&country=IN`,
      {
        headers: { Accept: 'application/json' },
      }
    );

    if (response.ok) {
      const data = await response.json();
      if (Array.isArray(data?.results) && data.results.length > 0) {
        const item = data.results[0];
        const coords = {
          lat: parseFloat(item.latitude),
          lon: parseFloat(item.longitude),
        };
        coordsCache[cacheKey] = coords;
        return coords;
      }
    }
  } catch (err) {
    console.warn(`Open-Meteo geocoding fallback failed for "${cityName}":`, err);
  }

  return null;
};

// Fallback Haversine straight-line distance multiplied by India road winding factor (~1.25)
const haversineDistanceKm = (lat1, lon1, lat2, lon2) => {
  const R = 6371; // Earth radius in km
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const aerialKm = R * c;
  return Math.round(aerialKm * 1.25);
};

const fetchRouteDistance = async (url) => {
  try {
    const res = await fetch(url, {
      headers: { 'User-Agent': 'PolystoxAdmin/1.0' },
    });
    if (res.ok) {
      const data = await res.json();
      if (data?.code === 'Ok' && Array.isArray(data?.routes) && data.routes.length > 0) {
        // Find the optimal/shortest highway driving route among alternatives
        const validDistances = data.routes
          .map((r) => Math.round(r.distance / 1000))
          .filter((d) => d > 0);

        if (validDistances.length > 0) {
          return {
            distanceKm: Math.min(...validDistances),
          };
        }
      }
    }
  } catch (err) {
    console.warn(`Routing endpoint failed: ${url}`, err);
  }
  return null;
};

/**
 * Calculate actual road driving distance (in km)
 * Supports Google Maps API with seamless fallback to Free OSRM & OpenStreetMap routing.
 */
export const calculateDistanceAndTransit = async (originCity, destCity) => {
  if (!originCity || !destCity) return null;

  const originClean = cleanCityString(originCity);
  const destClean = cleanCityString(destCity);
  if (!originClean || !destClean) return null;

  try {
    // Step 1: Check Google Maps JS API if available
    const googleResult = await fetchGoogleMapsDistance(originClean, destClean);
    if (googleResult && googleResult.distanceKm) {
      return { distanceKm: googleResult.distanceKm };
    }

    // Step 2: Free Geocoding (Nominatim / Open-Meteo)
    const [c1, c2] = await Promise.all([
      getCityCoordinates(originClean),
      getCityCoordinates(destClean),
    ]);

    if (!c1 || !c2) {
      console.warn(`Could not geocode: "${originClean}" or "${destClean}"`);
      return null;
    }

    // Step 3: Free Road Routing Engines (OSRM primary + OSM Germany fallback)
    const primaryUrl = `https://router.project-osrm.org/route/v1/driving/${c1.lon},${c1.lat};${c2.lon},${c2.lat}?overview=false&alternatives=true`;
    const fallbackUrl = `https://routing.openstreetmap.de/routed-car/route/v1/driving/${c1.lon},${c1.lat};${c2.lon},${c2.lat}?overview=false`;

    let routeResult = await fetchRouteDistance(primaryUrl);
    if (!routeResult) {
      routeResult = await fetchRouteDistance(fallbackUrl);
    }

    const distKm = routeResult?.distanceKm;
    if (distKm) {
      return {
        distanceKm: distKm,
        source: 'osrm',
      };
    }

    // Step 4: Fallback Haversine if external routing servers are temporarily unreachable
    const fallbackKm = haversineDistanceKm(c1.lat, c1.lon, c2.lat, c2.lon);

    return {
      distanceKm: fallbackKm,
      source: 'haversine-fallback',
    };
  } catch (error) {
    console.error('Error calculating distance:', error);
    return null;
  }
};
